import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { intercityApi, type IntercityRoute, type IntercityRouteInput, type IntercityUsage } from "@/services/pricing";
import { Card } from "@/shared/ui/Card";
import { LoadingState } from "@/shared/ui/PageHeader";
import { useCan } from "@/auth/permissions";
import { usdInputToCentsAllowZero, centsToUsdInput, fmtUsd } from "@/pages/products/transferCost";

/** INTERCITY-1 · 售价配置 →「城际用车价目」卡片。财务（owner / finance / tech_admin）维护，其他人只读；产品里只能下拉选这里的线路。 */

const INP = "h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px] focus:border-blue-500 focus:outline-none";
const BLANK = { city_a_cn: "", city_a_en: "", city_b_cn: "", city_b_en: "", price: "", status: "active" as "active" | "inactive" };
type Form = typeof BLANK;

const errText = (e: unknown, fallback: string) => {
  const m = (e as Error)?.message;
  return m && m !== "[object Object]" ? m : fallback;
};

function RouteModal({ route, canEdit, onClose }: { route: IntercityRoute | null; canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(route
    ? { city_a_cn: route.city_a_cn, city_a_en: route.city_a_en, city_b_cn: route.city_b_cn, city_b_en: route.city_b_en, price: centsToUsdInput(route.one_way_cents), status: route.status }
    : { ...BLANK });
  const [err, setErr] = useState<string | null>(null);
  const cents = usdInputToCentsAllowZero(f.price);
  const priceChanged = !!route && cents != null && cents !== route.one_way_cents;

  // 改价时：预览影响范围（后端按新价算 旧金额 → 新金额）
  const usageQ = useQuery<IntercityUsage>({
    queryKey: ["intercity", "usage", route?.id, cents],
    queryFn: () => intercityApi.usage(route!.id, cents as number),
    enabled: !!route && priceChanged,
  });
  const inUse = usageQ.data?.products ?? [];

  const save = useMutation({
    mutationFn: () => {
      const body: IntercityRouteInput = {
        city_a_cn: f.city_a_cn.trim(), city_a_en: f.city_a_en.trim(), city_b_cn: f.city_b_cn.trim(), city_b_en: f.city_b_en.trim(),
        one_way_cents: cents as number, status: f.status,
      };
      return route ? intercityApi.update(route.id, body) : intercityApi.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["intercity"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
      onClose();
    },
    onError: (e) => setErr(errText(e, "保存失败")),
  });
  const valid = f.city_a_cn.trim() && f.city_a_en.trim() && f.city_b_cn.trim() && f.city_b_en.trim() && cents != null;
  const field = (label: string, key: keyof Form, placeholder = "") => (
    <label className="flex flex-col gap-1"><span className="text-[12px] font-medium text-slate-600">{label} <span className="text-rose-600">*</span></span>
      <input className={INP} value={f[key]} placeholder={placeholder} disabled={!canEdit} onChange={(e) => setF({ ...f, [key]: e.target.value })} /></label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose} data-testid="intercity-modal-bg">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" role="dialog" aria-label={route ? "编辑城际线路" : "新增城际线路"} onClick={(e) => e.stopPropagation()}>
        <h4 className="mb-3 text-[15px] font-semibold text-slate-900">{route ? "编辑城际线路" : "新增城际线路"}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {field("城市 A（中文）", "city_a_cn", "如 洛杉矶")}
          {field("城市 A（英文）", "city_a_en", "如 Los Angeles")}
          {field("城市 B（中文）", "city_b_cn", "如 拉斯维加斯")}
          {field("城市 B（英文）", "city_b_en", "如 Las Vegas")}
          <label className="flex flex-col gap-1"><span className="text-[12px] font-medium text-slate-600">单程价 / 人 <span className="text-rose-600">*</span></span>
            <div className="relative"><span className="absolute left-3 top-2 text-[13px] text-slate-400">$</span>
              <input className={`${INP} pl-6 tabular-nums`} inputMode="decimal" value={f.price} placeholder="如 50" disabled={!canEdit} data-testid="intercity-price" onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
            {route && <span className="text-[11px] text-slate-400">原价 {fmtUsd(route.one_way_cents)}</span>}
          </label>
          <label className="flex flex-col gap-1"><span className="text-[12px] font-medium text-slate-600">状态</span>
            <select className={INP} value={f.status} disabled={!canEdit} onChange={(e) => setF({ ...f, status: e.target.value as Form["status"] })}>
              <option value="active">启用</option><option value="inactive">停用</option>
            </select></label>
        </div>
        {priceChanged && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-800" data-testid="intercity-impact">
            <b>保存后立刻生效：</b>
            {usageQ.isLoading ? "正在计算影响范围…" : usageQ.isError ? "影响范围加载失败（仍可保存，保存后同样立刻生效）。" : inUse.length === 0
              ? "目前没有产品在用这条线路。"
              : <>以下 {inUse.length} 个产品的成本和售价会重算，并推给已连接的平台；已下订单不变。
                <ul className="mt-1.5 list-disc pl-5">{inUse.map((p) => (
                  <li key={p.product_id}>{p.code} {p.name_cn}（{p.direction_label} {fmtUsd(p.old_amount_cents)} → {fmtUsd(p.new_amount_cents)}）</li>
                ))}</ul></>}
          </div>
        )}
        {err && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" data-testid="intercity-error">{err}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-[13px] text-slate-700 hover:bg-slate-50">{canEdit ? "取消" : "关闭"}</button>
          {canEdit && <button type="button" disabled={!valid || save.isPending} onClick={() => { setErr(null); save.mutate(); }} data-testid="intercity-save"
            className="h-8 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{route ? "保存并生效" : "添加"}</button>}
        </div>
      </div>
    </div>
  );
}

export function IntercityRoutesCard() {
  const canEdit = useCan("pricing.channel_config.update");   // 老板 / 财务 / 技术管理员
  const q = useQuery({ queryKey: ["intercity", "routes"], queryFn: () => intercityApi.list() });
  const [editing, setEditing] = useState<IntercityRoute | "new" | null>(null);

  return (
    <Card title="城际用车价目" action={canEdit ? (
      <button type="button" onClick={() => setEditing("new")} data-testid="intercity-add" className="flex h-8 items-center gap-1 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700"><Plus className="h-3.5 w-3.5" /> 新增线路</button>
    ) : undefined}>
      <p className="mb-3 max-w-3xl text-[12px] text-slate-500">两个城市之间的往返用车，按每人单程价算。产品里只能选这里的线路，选「往返」自动 × 2。改价后用到它的产品立刻重算售价并推给平台；已下的订单不变。</p>
      {q.isError ? (
        <div className="flex items-center justify-center gap-2 py-6 text-[13px] text-slate-500">加载失败 · <button onClick={() => q.refetch()} className="font-medium text-blue-600 hover:underline">重试</button></div>
      ) : q.isLoading ? <LoadingState /> : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full min-w-[640px] text-[13px]" aria-label="城际用车价目">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3 text-left">线路</th><th className="px-4 py-3 text-right">单程价 / 人</th><th className="px-4 py-3 text-right">往返 / 人</th><th className="px-4 py-3 text-left">在用产品</th><th className="px-4 py-3 text-left">状态</th><th className="px-4 py-3 text-left">最近修改</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {q.data?.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid="intercity-row">
                  <td className="px-4 py-3"><div className="font-medium text-slate-800">{r.name_cn}</div><div className="text-[11.5px] text-slate-400">{r.name_en}</div></td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-800">{fmtUsd(r.one_way_cents)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-800">{fmtUsd(r.round_trip_cents)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.in_use_products} 个</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.status === "active" ? "启用" : "停用"}</span></td>
                  <td className="px-4 py-3 text-[11.5px] text-slate-400">{[r.updated_by_name, r.updated_at ? new Date(r.updated_at).toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" }) : null].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3 text-right"><button type="button" onClick={() => setEditing(r)} className="rounded-md border border-slate-200 px-2 py-1 text-[12px] text-slate-700 hover:bg-slate-50">{canEdit ? "编辑" : "查看"}</button></td>
                </tr>
              ))}
              {q.data?.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">还没有城际线路{canEdit ? "，点右上角「新增线路」添加" : "，请找财务添加"}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate-400">改价记入操作日志（谁、何时、旧价 → 新价）。有产品在用的线路不能停用，要先在产品里去掉。{!canEdit && "只有老板 / 财务 / 技术管理员能编辑。"}</div>
      {editing && <RouteModal route={editing === "new" ? null : editing} canEdit={canEdit} onClose={() => setEditing(null)} />}
    </Card>
  );
}
